import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, ArrowRight, PlayCircle, BookOpen } from "lucide-react";
import { authHeader, clearUserToken, getUserToken } from "@/utils/userAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursesIndexPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getUserToken()) {
      navigate("/?login=1");
      return;
    }
    Promise.all([
      axios.get(`${API}/users/me`, { headers: authHeader() }),
      axios.get(`${API}/courses`),
    ])
      .then(([me, cs]) => { setUser(me.data); setCourses(cs.data || []); })
      .catch(() => { clearUserToken(); navigate("/?login=1"); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => { clearUserToken(); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}>
        <Loader2 className="animate-spin" size={26} style={{ color: "var(--np-secondary)" }} />
      </div>
    );
  }

  const firstName = user?.name?.split(" ")[0] || "studente";
  const unlockedSomewhere = (c) => user && (user.has_access_l1 || user.has_access_l2 || user.has_access_l3 || user.has_access_full_course);

  return (
    <main className="min-h-screen pb-32" style={{ background: "var(--np-tertiary)" }} data-testid="courses-index">
      <header className="border-b" style={{ background: "var(--np-quaternary)", borderColor: "rgba(26,42,58,0.06)" }}>
        <div className="mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between" style={{ maxWidth: 1280 }}>
          <Link to="/" aria-label="Neuroscienza Pratica" className="inline-flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/ypgcjh4z_11%20%2829%29.png"
              alt="Neuroscienza Pratica"
              className="block"
              style={{ width: "clamp(220px, 24vw, 380px)", aspectRatio: "4 / 1", objectFit: "cover", objectPosition: "center" }}
            />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm" style={{ color: "var(--np-muted)" }}>
              Ciao, <strong style={{ color: "var(--np-primary)" }}>{firstName}</strong>
            </span>
            <button data-testid="logout-btn" onClick={handleLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition hover:bg-white" style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto px-6 pt-14 md:pt-20" style={{ maxWidth: 1100 }}>
        <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "var(--np-secondary)" }}>
          I tuoi corsi
        </p>
        <h1 className="text-3xl md:text-5xl leading-[1.05] tracking-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          Bentornato, {firstName}
        </h1>
        <p className="mt-4 text-base md:text-lg leading-relaxed max-w-3xl" style={{ color: "var(--np-muted)" }}>
          Scegli il corso da seguire. Dentro ogni corso puoi navigare libero fra le lezioni disponibili.
        </p>
      </section>

      <section className="mx-auto px-6 pt-10" style={{ maxWidth: 1100 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {courses.map((c) => (
            <CourseCard key={c.id} course={c} hasAccess={unlockedSomewhere(c)} />
          ))}
        </div>
        {courses.length === 0 && (
          <p className="text-center text-sm py-12" style={{ color: "var(--np-muted)" }}>
            Nessun corso disponibile al momento.
          </p>
        )}
      </section>
    </main>
  );
}

function CourseCard({ course, hasAccess }) {
  // For now there's a single course → Members Area; future-proofed for multi-course routing.
  const targetUrl = course.id === "figlio-a-carico" ? "/corso/area-membri" : `/corso/${course.id}/area-membri`;
  return (
    <Link
      to={targetUrl}
      data-testid={`course-card-${course.id}`}
      className="block rounded-2xl bg-white border overflow-hidden transition hover:shadow-lg hover:-translate-y-0.5"
      style={{ borderColor: "rgba(217,94,57,0.18)" }}
    >
      <div className="aspect-[2/1] bg-[#1A2A3A] flex items-center justify-center" style={{ background: "var(--np-quaternary)" }}>
        <img
          src={course.cover_image_url}
          alt={course.title}
          className="block w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center" }}
        />
      </div>
      <div className="p-6">
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-1.5" style={{ color: "var(--np-secondary)" }}>
          {course.category} · {course.total_lessons} lezioni
        </p>
        <h3 className="text-xl md:text-2xl leading-snug font-semibold" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          {course.title}
        </h3>
        {course.subtitle && (
          <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--np-muted)" }}>
            {course.subtitle}
          </p>
        )}
        <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--np-secondary)" }}>
          {hasAccess ? (<><PlayCircle size={15} /> Continua il corso</>) : (<><BookOpen size={15} /> Scopri il corso</>)}
          <ArrowRight size={14} strokeWidth={2.5} />
        </div>
      </div>
    </Link>
  );
}
